import React, { useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import cccApi, { AssinadoDadosData, ProspeccaoLead } from '../api/cccApi';
import { ASSINATURA_NAO_MOTIVOS } from '../config/assinadoDados';
import { readFlatMotivoKeys, toggleMotivoKey } from '../utils/motivoKeys';

type Props = {
  leadId: string;
  initial?: AssinadoDadosData | null;
  disabled?: boolean;
  onSaved?: (lead: ProspeccaoLead) => void;
};

const formatSavedAt = (iso?: string | null) => {
  if (!iso) return null;
  try {
    return new Date(iso.replace('Z', '')).toLocaleString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const ProspeccaoAssinadoForm: React.FC<Props> = ({ leadId, initial, disabled, onSaved }) => {
  const motivoLabelMap = useMemo(
    () => new Map(ASSINATURA_NAO_MOTIVOS.map((m) => [m.key, m.label])),
    []
  );

  const [assinado, setAssinado] = useState<boolean | null>(
    typeof initial?.assinado === 'boolean' ? initial.assinado : null
  );
  const [dataAssinatura, setDataAssinatura] = useState(initial?.data_assinatura || '');
  const [motivoKeys, setMotivoKeys] = useState<string[]>(() =>
    readFlatMotivoKeys(initial as Record<string, unknown>, 'motivo_nao_assinado_keys', 'motivo_nao_assinado')
  );
  const [motivoNaoAssinadoOutro, setMotivoNaoAssinadoOutro] = useState(
    initial?.motivo_nao_assinado_outro || ''
  );
  const [fornecedorAtual, setFornecedorAtual] = useState(initial?.fornecedor_atual || '');
  const [dataFimContrato, setDataFimContrato] = useState(initial?.data_fim_contrato || '');
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState<{
    updated_at?: string | null;
    updated_by_name?: string | null;
  }>({
    updated_at: initial?.updated_at,
    updated_by_name: initial?.updated_by_name,
  });

  useEffect(() => {
    setAssinado(typeof initial?.assinado === 'boolean' ? initial.assinado : null);
    setDataAssinatura(initial?.data_assinatura || '');
    setMotivoKeys(
      readFlatMotivoKeys(initial as Record<string, unknown>, 'motivo_nao_assinado_keys', 'motivo_nao_assinado')
    );
    setMotivoNaoAssinadoOutro(initial?.motivo_nao_assinado_outro || '');
    setFornecedorAtual(initial?.fornecedor_atual || '');
    setDataFimContrato(initial?.data_fim_contrato || '');
    setMeta({
      updated_at: initial?.updated_at,
      updated_by_name: initial?.updated_by_name,
    });
  }, [
    leadId,
    initial?.assinado,
    initial?.data_assinatura,
    initial?.motivo_nao_assinado,
    initial?.motivo_nao_assinado_outro,
    initial?.fornecedor_atual,
    initial?.data_fim_contrato,
    initial?.updated_at,
    initial?.updated_by_name,
  ]);

  const handleSave = async () => {
    if (disabled || saving) return;
    setSaving(true);
    try {
      const res = await cccApi.updateProspeccaoAssinadoDados(leadId, {
        assinado,
        data_assinatura: dataAssinatura.trim() || null,
        motivo_nao_assinado_keys: motivoKeys,
        motivo_nao_assinado: motivoKeys[0] || null,
        motivo_nao_assinado_outro: motivoNaoAssinadoOutro.trim() || null,
        fornecedor_atual: fornecedorAtual.trim() || null,
        data_fim_contrato: dataFimContrato.trim() || null,
      });
      if (!res.success || !res.lead) {
        message.error(res.message || 'Não foi possível guardar os dados de assinatura');
        return;
      }
      const saved = res.lead.assinado_dados;
      if (saved) {
        setMeta({
          updated_at: saved.updated_at,
          updated_by_name: saved.updated_by_name,
        });
      }
      onSaved?.(res.lead);
      message.success('Dados de assinatura guardados');
    } finally {
      setSaving(false);
    }
  };

  const savedLabel = formatSavedAt(meta.updated_at);

  return (
    <div className="rec-envio-proposta">
      <div className="rec-envio-proposta-check">
        <span>
          Assinado? <span className="rec-required-asterisk">*</span>
        </span>
        <div style={{ display: 'flex', gap: 16 }}>
          <label>
            <input
              type="radio"
              name={`prosp-assinado-${leadId}`}
              checked={assinado === true}
              disabled={disabled}
              onChange={() => setAssinado(true)}
            />{' '}
            Sim
          </label>
          <label>
            <input
              type="radio"
              name={`prosp-assinado-${leadId}`}
              checked={assinado === false}
              disabled={disabled}
              onChange={() => setAssinado(false)}
            />{' '}
            Não
          </label>
        </div>
      </div>

      {assinado === true && (
        <div className="rec-envio-proposta-fields">
          <label className="rec-envio-proposta-field">
            <span className="rec-envio-proposta-label">
              Data de Assinatura <span className="rec-required-asterisk">*</span>
            </span>
            <input
              type="date"
              className="rec-envio-proposta-input"
              value={dataAssinatura}
              disabled={disabled}
              onChange={(e) => setDataAssinatura(e.target.value)}
            />
          </label>
        </div>
      )}

      {assinado === false && (
        <div className="rec-motivo">
          <fieldset className="rec-motivo-category" disabled={disabled}>
            <legend className="rec-motivo-category-title">
              Motivo <span className="rec-required-asterisk">*</span>
            </legend>
            <div className="rec-motivo-options">
              {ASSINATURA_NAO_MOTIVOS.map((opt) => {
                const isSelected = motivoKeys.includes(opt.key);
                return (
                  <button
                    key={opt.key}
                    type="button"
                    className={`rec-motivo-option ${isSelected ? 'is-selected' : ''}`}
                    aria-pressed={isSelected}
                    onClick={() => setMotivoKeys((prev) => toggleMotivoKey(prev, opt.key))}
                  >
                    <span className="rec-motivo-option-check" aria-hidden>
                      {isSelected ? '✓' : ''}
                    </span>
                    <span>{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </fieldset>
          <div className="rec-motivo-feedback-block">
            <label
              className="rec-motivo-feedback-label"
              htmlFor={`prosp-assinatura-motivo-outro-${leadId}`}
            >
              Comentário adicional
              {motivoKeys.includes('outro') && <span className="rec-required-asterisk"> *</span>}
            </label>
            <textarea
              id={`prosp-assinatura-motivo-outro-${leadId}`}
              className="rec-motivo-feedback-input"
              rows={4}
              value={motivoNaoAssinadoOutro}
              disabled={disabled}
              placeholder="Detalhe o motivo, contexto ou próximos passos…"
              onChange={(e) => setMotivoNaoAssinadoOutro(e.target.value)}
            />
          </div>
          {motivoKeys.includes('fidelizado') && (
            <div className="rec-envio-proposta-fields">
              <label className="rec-envio-proposta-field">
                <span className="rec-envio-proposta-label">
                  Fornecedor Atual <span className="rec-required-asterisk">*</span>
                </span>
                <input
                  type="text"
                  className="rec-envio-proposta-input"
                  value={fornecedorAtual}
                  disabled={disabled}
                  placeholder="Nome do fornecedor atual"
                  onChange={(e) => setFornecedorAtual(e.target.value)}
                />
              </label>
              <label className="rec-envio-proposta-field">
                <span className="rec-envio-proposta-label">
                  Data de Fim do Contrato <span className="rec-required-asterisk">*</span>
                </span>
                <input
                  type="date"
                  className="rec-envio-proposta-input"
                  value={dataFimContrato}
                  disabled={disabled}
                  onChange={(e) => setDataFimContrato(e.target.value)}
                />
              </label>
            </div>
          )}
        </div>
      )}

      <div className="rec-envio-proposta-actions">
        <button
          type="button"
          className="rec-btn rec-btn-primary"
          disabled={disabled || saving}
          onClick={handleSave}
        >
          {saving ? 'A guardar…' : 'Guardar'}
        </button>
      </div>

      {savedLabel && (
        <p className="rec-motivo-meta">
          {assinado === false && motivoKeys.length > 0 ? (
            <>
              Motivos: {motivoKeys.map((k) => motivoLabelMap.get(k) || k).join(', ')}
              {motivoKeys.includes('outro') && motivoNaoAssinadoOutro
                ? ` — ${motivoNaoAssinadoOutro}`
                : ''}
              {motivoKeys.includes('fidelizado') && (fornecedorAtual || dataFimContrato)
                ? ` — ${fornecedorAtual || 'Fornecedor por definir'}${dataFimContrato ? ` (fim: ${dataFimContrato})` : ''}`
                : ''}
              <br />
            </>
          ) : null}
          Última atualização{meta.updated_by_name ? ` por ${meta.updated_by_name}` : ''}: {savedLabel}
        </p>
      )}
    </div>
  );
};

export default ProspeccaoAssinadoForm;
